import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Send, MessageCircle, ChevronDown, Trash2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import SupplyMonitor from "./supply/SupplyMonitor";
const KYC_LABELS = {
  pending: { label: "Menunggu KYC", cls: "text-[#D4AF37] border-[#D4AF37]" },
  approved: { label: "Disetujui", cls: "text-[#5BE38B] border-[#5BE38B]" },
  rejected: { label: "Ditolak", cls: "text-[#F06464] border-[#F06464]" },
};

const linkCls = "inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 transition-colors";

function StockStats({ sid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/admin/suppliers-monitor/${sid}/stock`).then((r) => setRows(r.data)); }, [sid]);
  if (rows === null) return <p className="text-xs text-[#A8A8AD] py-2">Memuat stok...</p>;
  if (!rows.length) return <p data-testid={`smon-stock-empty-${sid}`} className="text-xs text-[#A8A8AD] py-2">Belum pernah mencatat stok.</p>;
  return (
    <table data-testid={`smon-stock-${sid}`} className="w-full text-xs my-2">
      <thead><tr className="text-left uppercase tracking-widest text-[#A8A8AD]"><th className="py-1">Produk</th><th className="text-right">Total dicatat</th><th className="text-right">Jumlah catatan</th><th className="text-right">Terakhir</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.product_id} data-testid={`smon-stock-row-${sid}-${r.product_id}`} className="border-t border-[#35353B]/60">
          <td className="py-1.5 text-[#F5F3ED]">{r.product_name}</td>
          <td className="text-right font-bold text-[#D4AF37]">{r.submitted} {r.unit}</td>
          <td className="text-right">{r.records}</td>
          <td className="text-right">{r.last_at ? new Date(r.last_at).toLocaleDateString("id-ID") : "-"}</td>
        </tr>))}</tbody>
    </table>
  );
}

function SupplierRow({ s, isOwner, onDeleted }) {
  const [open, setOpen] = useState(false);
  const del = async () => {
    if (!window.confirm(`Hapus akun supplier ${s.name}? Akun tidak bisa login lagi. Riwayat stok tetap tersimpan.`)) return;
    try { await api.delete(`/users/suppliers/${s.id}`); toast.success("Akun supplier dihapus"); onDeleted(); } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menghapus"); }
  };
  const kyc = KYC_LABELS[s.kyc_status];
  const blocked = s.banned || s.active === false;
  return (
    <>
    <tr data-testid={`smon-row-${s.id}`} className="border-b border-[#35353B]">
      <td className="py-3 pr-3">
        <div className="text-[#F5F3ED]">{s.name}</div>
        <div className="text-xs text-[#A8A8AD]">{s.email}</div>
      </td>
      <td className="py-3 pr-3">
        {kyc ? <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${kyc.cls}`}>{kyc.label}</span> : <span className="text-xs text-[#A8A8AD]">-</span>}
        {blocked && <span data-testid={`smon-blocked-${s.id}`} className="ml-2 text-[10px] uppercase tracking-widest text-[#F06464]">Diblokir</span>}
      </td>
      <td className="py-3 flex flex-wrap gap-2">
        {s.telegram && <a data-testid={`smon-telegram-${s.id}`} href={`https://t.me/${s.telegram}`} target="_blank" rel="noreferrer" className={`${linkCls} bg-[#229ED9]/15 text-[#5FC3F0] hover:bg-[#229ED9]/30`}><Send size={13} /> @{s.telegram}</a>}
        {s.whatsapp && <a data-testid={`smon-whatsapp-${s.id}`} href={`https://wa.me/${s.whatsapp}`} target="_blank" rel="noreferrer" className={`${linkCls} bg-[#25D366]/15 text-[#5BE38B] hover:bg-[#25D366]/30`}><MessageCircle size={13} /> {s.whatsapp}</a>}
        {!s.telegram && !s.whatsapp && <span data-testid={`smon-nocontact-${s.id}`} className="text-xs text-[#F2D98D]">Belum isi kontak (diminta saat login)</span>}
      </td>
      <td className="py-3 text-right whitespace-nowrap">
        <button data-testid={`smon-stock-toggle-${s.id}`} onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:text-[#F2D98D] transition-colors">Catatan Stok{s.stock_records > 0 && <span className="ml-1 bg-[#35353B] text-[#F5F3ED] rounded-full px-1.5 text-[10px] font-bold">{s.stock_records}</span>}<ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>
        {isOwner && <button data-testid={`smon-delete-${s.id}`} onClick={del} title="Hapus akun supplier" className="ml-3 text-[#A8A8AD] hover:text-[#F06464] transition-colors align-middle"><Trash2 size={14} /></button>}
      </td>
    </tr>
    {open && <tr className="border-b border-[#35353B] bg-[#191A1F]/60"><td colSpan="4" className="px-3"><StockStats sid={s.id} /></td></tr>}
    </>
  );
}

export default function SupplierMonitorPanel() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/suppliers-monitor").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const term = q.trim().toLowerCase();
  const list = (rows || []).filter((s) => !term || [s.name, s.email, s.telegram, s.whatsapp].some((v) => (v || "").toLowerCase().includes(term)));
  return (
    <div data-testid="supplier-monitor-panel" className="bg-[#202127] border border-[#35353B] rounded-lg p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-[#F5F3ED]" style={{ fontFamily: "'Playfair Display', serif" }}>Monitoring Supplier</h2>
        <div className="relative sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A8AD]" />
          <input data-testid="smon-search-input" placeholder="Cari nama, email, Telegram, WA" value={q} onChange={(e) => setQ(e.target.value)}
            className="bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] focus:border-[#D4AF37] rounded-sm pl-9 pr-3 py-2 w-full outline-none text-sm" />
        </div>
      </div>
      <SupplyMonitor />
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Semua Supplier</div>
      {rows === null ? <p className="text-sm text-[#A8A8AD]">Memuat...</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-[#A8A8AD] border-b border-[#35353B]"><th className="py-2">Supplier</th><th>Status</th><th>Hubungi</th><th /></tr></thead>
            <tbody>{list.map((s) => <SupplierRow key={s.id} s={s} isOwner={user?.role === "owner"} onDeleted={load} />)}</tbody>
          </table>
          {list.length === 0 && <p data-testid="smon-empty" className="text-sm text-[#A8A8AD] py-4">Tidak ada supplier.</p>}
          <p data-testid="smon-count" className="text-xs text-[#A8A8AD] mt-3">{list.length} supplier</p>
        </div>
      )}
    </div>
  );
}
